import { DialogComponent } from '@theme/dialog';

const MOBILE_BREAKPOINT = 750;
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const OPEN_HASH = '#open-newsletter';

/**
 * Newsletter popup with a CAPTCHA-compatible Shopify customer form.
 *
 * Shopify protects customer forms with hCaptcha. A direct fetch to /contact
 * bypasses Shopify's form protection and is rejected with HTTP 400. Shopify's
 * production CSP also prevents its response pages from loading in an iframe, so
 * the form uses the supported top-level submission and restores the popup's
 * success state after Shopify returns to the storefront.
 *
 * @extends DialogComponent
 */
class NewsletterPopupComponent extends DialogComponent {
  /** @type {number|undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('dialog:close', this.#handleClose);
    this.addEventListener('submit', this.#handleSubmit);
    window.addEventListener('hashchange', this.#handleHashChange);
    document.addEventListener('click', this.#handleOpenLink);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#handleSectionSelect);
      document.addEventListener('shopify:section:deselect', this.#handleSectionDeselect);
      return;
    }

    if (this.#handleReturnedSubmission()) return;
    if (this.#openFromHash()) return;
    if (!this.#shouldShow()) return;

    // Product pages: no timed popup. Show it on exit intent instead (desktop only:
    // there is no reliable exit-intent signal on touch devices, so mobile never auto-opens it).
    if (this.exitIntentOnly) {
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        document.documentElement.addEventListener('mouseleave', this.#handleExitIntent);
      }
      return;
    }

    this.#timeout = setTimeout(() => this.showDialog(), this.delay);
  }

  get exitIntentOnly() {
    return this.hasAttribute('data-exit-intent-only');
  }

  /**
   * Fires when the cursor leaves the page through the top edge (towards tabs / address bar / close).
   * @param {MouseEvent} event
   */
  #handleExitIntent = (event) => {
    if (event.relatedTarget || event.clientY > 0) return;
    document.documentElement.removeEventListener('mouseleave', this.#handleExitIntent);
    if (this.#shouldShow()) this.showDialog();
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    clearTimeout(this.#timeout);
    document.documentElement.removeEventListener('mouseleave', this.#handleExitIntent);
    this.removeEventListener('dialog:close', this.#handleClose);
    this.removeEventListener('submit', this.#handleSubmit);
    window.removeEventListener('hashchange', this.#handleHashChange);
    document.removeEventListener('click', this.#handleOpenLink);
    document.removeEventListener('shopify:section:select', this.#handleSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.#handleSectionDeselect);
  }

  get delay() {
    const seconds = Number.parseFloat(`${this.dataset.delay}`);
    return Number.isNaN(seconds) ? 0 : seconds * 1000;
  }

  get frequency() {
    const days = Number.parseFloat(`${this.dataset.frequencyDays}`);
    return Number.isNaN(days) ? 0 : days * DAY_IN_MS;
  }

  get storageKey() {
    return this.dataset.storageKey ?? 'newsletter-popup';
  }

  #shouldShow() {
    if (this.hasAttribute('data-desktop-only') && window.innerWidth < MOBILE_BREAKPOINT) return false;
    if (!this.frequency) return true;

    const dismissedAt = Number(this.#read());
    return !dismissedAt || Date.now() - dismissedAt > this.frequency;
  }

  #read() {
    try {
      return window.localStorage.getItem(this.storageKey);
    } catch (_) {
      return null;
    }
  }

  #remember = () => {
    try {
      window.localStorage.setItem(this.storageKey, `${Date.now()}`);
    } catch (_) {}
  };

  get #submissionKey() {
    return `${this.storageKey}:pending-email`;
  }

  #takePendingEmail() {
    try {
      const email = window.sessionStorage.getItem(this.#submissionKey);
      window.sessionStorage.removeItem(this.#submissionKey);
      return email;
    } catch (_) {
      return null;
    }
  }

  #handleReturnedSubmission() {
    const url = new URL(window.location.href);
    if (url.searchParams.get('newsletter_popup') !== 'success') return false;

    const email = this.#takePendingEmail();
    url.searchParams.delete('newsletter_popup');
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`
    );

    if (email === null) return false;

    const form = /** @type {HTMLFormElement|null} */ (
      this.querySelector('.newsletter-popup__form')
    );
    const template = /** @type {HTMLTemplateElement|null} */ (
      form?.querySelector('[data-popup-success-template]')
    );
    const result = /** @type {HTMLElement|null} */ (
      template?.content.firstElementChild?.cloneNode(true) ?? null
    );

    if (!form || !result) return false;

    form.querySelector('[data-popup-state]')?.remove();
    form.appendChild(result);
    form.classList.add('is-success');
    form.reset();
    this.#remember();
    this.#trackLead(email);
    this.showDialog();
    requestAnimationFrame(() => result.focus());

    return true;
  }

  #handleClose = () => {
    this.#remember();

    // Remove the explicit open trigger after dismissal. This prevents a refresh
    // of the same URL from reopening the popup while the 30-day dismissal is active.
    if (window.location.hash === OPEN_HASH) {
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${window.location.search}`
      );
    }
  };

  #openFromHash() {
    if (window.location.hash !== OPEN_HASH) return false;

    clearTimeout(this.#timeout);
    this.showDialog();
    return true;
  }

  #handleHashChange = () => {
    this.#openFromHash();
  };

  /** @param {MouseEvent} event */
  #handleOpenLink = (event) => {
    const target = event.target;
    const link = target instanceof Element ? target.closest('a[href]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;

    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || url.hash !== OPEN_HASH) return;

    // Links to another storefront page should navigate normally; that page will
    // open the popup from its initial hash. Handle same-page links directly so
    // clicking the same hash repeatedly still works.
    if (url.pathname === window.location.pathname && url.search === window.location.search) {
      event.preventDefault();

      if (window.location.hash !== OPEN_HASH) {
        window.history.pushState(window.history.state, '', OPEN_HASH);
      }

      clearTimeout(this.#timeout);
      this.showDialog();
    }
  };

  /**
   * Do not prevent this event. Shopify must receive it so its hCaptcha handler
   * can add h-captcha-response and perform a supported top-level submission.
   *
   * @param {SubmitEvent} event
   */
  #handleSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const email =
      /** @type {HTMLInputElement|null} */ (form.querySelector('input[name="contact[email]"]'))?.value ?? '';
    try {
      window.sessionStorage.setItem(this.#submissionKey, email);
    } catch (_) {}

    this.toggleAttribute('loading', true);
  };

  /** @param {string} email */
  #trackLead(email) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'generate_lead',
      form_type: 'newsletter_popup',
      user_email: email || undefined,
    });
  }

  /** @param {Event} event */
  #handleSectionSelect = (event) => {
    if (this.#isOwnSection(event)) this.showDialog();
  };

  /** @param {Event} event */
  #handleSectionDeselect = (event) => {
    if (this.#isOwnSection(event)) this.closeDialog();
  };

  /** @param {Event} event */
  #isOwnSection(event) {
    return event.target instanceof Node && event.target.contains(this);
  }
}

if (!customElements.get('newsletter-popup-component')) {
  customElements.define('newsletter-popup-component', NewsletterPopupComponent);
}
